import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Field, Input, PasswordInput, Select, Textarea } from "@/components/base/FormControls";
import Toggle from "@/components/base/Toggle";
import { cn } from "@/utils/cn";
import { useSession } from "@/session/SessionProvider";
import { fullName, ROLE_LABELS } from "@/session/types";
import { useChangePassword } from "@/hooks/useChangePassword";
import {
  DUREE_SESSION_OPTIONS,
  DEVISE_OPTIONS,
  FORMAT_DATE_OPTIONS,
  FUSEAU_OPTIONS,
  LANGUE_OPTIONS,
  usePlatformSettings,
} from "@/store/platformSettings/PlatformSettingsProvider";

function SectionCard({
  icon,
  title,
  description,
  children,
  footer,
}: {
  icon: string;
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-start gap-3 border-b border-background-200 px-4 py-3.5 md:px-5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
          <i className={cn(icon, "text-base")} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{title}</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">{description}</p>
        </div>
      </header>
      <div className="p-4 md:p-5">{children}</div>
      {footer && <div className="border-t border-background-200 px-4 py-3.5 md:px-5">{footer}</div>}
    </section>
  );
}

function SaveRow({ onSave, saved }: { onSave: () => void; saved: string | null }) {
  return (
    <div className="flex items-center justify-between gap-3">
      {saved && (
        <span className="inline-flex items-center gap-1.5 text-xs font-label font-medium text-secondary-700">
          <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
          {saved}
        </span>
      )}
      {!saved && <span className="text-xs text-foreground-400">Les modifications restent locales (frontend).</span>}
      <button
        type="button"
        onClick={onSave}
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
      >
        <i className="ri-save-line text-base" aria-hidden="true" />
        Enregistrer
      </button>
    </div>
  );
}

export default function SuperAdminParametresPage() {
  const navigate = useNavigate();
  const { session, logout } = useSession();
  const { settings, updateGeneral, updatePlateforme, updateNotifications, updateSecurite } = usePlatformSettings();
  const { changePassword, saving: pwdSaving } = useChangePassword();

  const [general, setGeneral] = useState(settings.general);
  const [plateforme, setPlateforme] = useState(settings.plateforme);
  const [savedGeneral, setSavedGeneral] = useState<string | null>(null);
  const [savedPlateforme, setSavedPlateforme] = useState<string | null>(null);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSaved, setPwdSaved] = useState(false);

  const saveGeneral = () => {
    updateGeneral(general);
    setSavedGeneral("Informations enregistrées.");
    window.setTimeout(() => setSavedGeneral(null), 2500);
  };

  const savePlateforme = () => {
    updatePlateforme(plateforme);
    setSavedPlateforme("Préférences enregistrées.");
    window.setTimeout(() => setSavedPlateforme(null), 2500);
  };

  const handlePassword = async () => {
    setPwdSaved(false);
    if (!pwd.current || !pwd.next || !pwd.confirm) {
      setPwdError("Tous les champs sont obligatoires.");
      return;
    }
    if (pwd.next.length < 6) {
      setPwdError("Le nouveau mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setPwdError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setPwdError(null);
    const result = await changePassword(session?.email ?? "", pwd.current, pwd.next);
    if (!result.ok) {
      setPwdError(result.error);
      return;
    }
    setPwd({ current: "", next: "", confirm: "" });
    setPwdSaved(true);
    window.setTimeout(() => setPwdSaved(false), 3000);
  };

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-3xl">
        <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Paramètres</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
          Paramètres de la plateforme et de votre compte administrateur. Votre accès est authentifié par Supabase :
          vous pouvez y modifier votre mot de passe. Les préférences d'affichage et de notification restent des
          réglages d'interface tant qu'aucune colonne dédiée n'existe en base.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <SectionCard
          icon="ri-global-line"
          title="Informations générales"
          description="Identité de la plateforme utilisée sur les documents et l'interface."
          footer={<SaveRow onSave={saveGeneral} saved={savedGeneral} />}
        >
          <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
            <Field label="Nom de la plateforme" htmlFor="set-nom" className="sm:col-span-2">
              <Input id="set-nom" value={general.nom} onChange={(e) => setGeneral({ ...general, nom: e.target.value })} maxLength={80} />
            </Field>
            <Field label="Email de contact" htmlFor="set-email">
              <Input id="set-email" type="email" value={general.email} onChange={(e) => setGeneral({ ...general, email: e.target.value })} />
            </Field>
            <Field label="Téléphone" htmlFor="set-telephone">
              <Input id="set-telephone" value={general.telephone} onChange={(e) => setGeneral({ ...general, telephone: e.target.value })} maxLength={30} />
            </Field>
            <Field label="Adresse" htmlFor="set-adresse" className="sm:col-span-2">
              <Input id="set-adresse" value={general.adresse} onChange={(e) => setGeneral({ ...general, adresse: e.target.value })} maxLength={160} />
            </Field>
            <Field label="Description" htmlFor="set-description" className="sm:col-span-2">
              <Textarea
                id="set-description"
                value={general.description}
                onChange={(e) => setGeneral({ ...general, description: e.target.value })}
                className="min-h-[80px]"
                maxLength={400}
              />
            </Field>
          </div>
        </SectionCard>

        <div className="flex flex-col gap-5">
          <SectionCard
            icon="ri-equalizer-line"
            title="Préférences d'affichage"
            description="Devise, langue, fuseau horaire et format des dates."
            footer={<SaveRow onSave={savePlateforme} saved={savedPlateforme} />}
          >
            <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
              <Field label="Devise" htmlFor="set-devise">
                <Select id="set-devise" value={plateforme.devise} onChange={(e) => setPlateforme({ ...plateforme, devise: e.target.value })} options={DEVISE_OPTIONS} />
              </Field>
              <Field label="Langue" htmlFor="set-langue">
                <Select id="set-langue" value={plateforme.langue} onChange={(e) => setPlateforme({ ...plateforme, langue: e.target.value })} options={LANGUE_OPTIONS} />
              </Field>
              <Field label="Fuseau horaire" htmlFor="set-fuseau">
                <Select id="set-fuseau" value={plateforme.fuseau} onChange={(e) => setPlateforme({ ...plateforme, fuseau: e.target.value })} options={FUSEAU_OPTIONS} />
              </Field>
              <Field label="Format de date" htmlFor="set-format">
                <Select id="set-format" value={plateforme.formatDate} onChange={(e) => setPlateforme({ ...plateforme, formatDate: e.target.value })} options={FORMAT_DATE_OPTIONS} />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            icon="ri-notification-3-line"
            title="Notifications"
            description="Alertes administratives et rappels envoyés par la plateforme."
          >
            <div className="flex flex-col gap-4">
              <Toggle
                label="Email d'activation"
                description="Notifier la création et l'activation des comptes."
                checked={settings.notifications.emailActivation}
                onChange={(value) => updateNotifications({ emailActivation: value })}
              />
              <Toggle
                label="Alertes abonnement"
                description="Prévenir avant l'expiration d'un abonnement."
                checked={settings.notifications.alerteAbonnement}
                onChange={(value) => updateNotifications({ alerteAbonnement: value })}
              />
              <Toggle
                label="Alertes paiement"
                description="Signaler les paiements en attente ou refusés."
                checked={settings.notifications.alertePaiement}
                onChange={(value) => updateNotifications({ alertePaiement: value })}
              />
              <Toggle
                label="Rapport hebdomadaire"
                description="Recevoir une synthèse hebdomadaire de l'activité."
                checked={settings.notifications.rapportHebdo}
                onChange={(value) => updateNotifications({ rapportHebdo: value })}
              />
            </div>
          </SectionCard>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <SectionCard
          icon="ri-shield-keyhole-line"
          title="Sécurité"
          description="Session et préférences de sécurité de votre compte administrateur."
        >
          <div className="flex flex-col gap-4">
            <Field label="Durée de session" htmlFor="set-session">
              <Select
                id="set-session"
                value={settings.securite.dureeSession}
                onChange={(e) => updateSecurite({ dureeSession: e.target.value })}
                options={DUREE_SESSION_OPTIONS}
              />
            </Field>
            <Toggle
              label="Double authentification"
              description="Exiger un second facteur à la connexion (à brancher avec Supabase)."
              checked={settings.securite.doubleAuthentification}
              onChange={(value) => updateSecurite({ doubleAuthentification: value })}
            />
            <Toggle
              label="Journalisation"
              description="Conserver l'historique des actions administratives dans le journal d'audit."
              checked={settings.securite.journalisation}
              onChange={(value) => updateSecurite({ journalisation: value })}
            />
            <Toggle
              label="Mode maintenance"
              description="Préparer l'affichage d'une page de maintenance (à brancher avec le backend)."
              checked={settings.securite.maintenance}
              onChange={(value) => updateSecurite({ maintenance: value })}
            />
          </div>
        </SectionCard>

        <SectionCard
          icon="ri-user-settings-line"
          title="Compte administrateur"
          description="Profil, changement de mot de passe et déconnexion."
        >
          <div className="flex flex-col gap-5">
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-foreground-500">Nom</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">
                  {session ? fullName(session) : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Rôle</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">
                  {session ? ROLE_LABELS[session.role] : "Super administrateur"}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs text-foreground-500">Email</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">{session?.email ?? "—"}</dd>
              </div>
            </dl>

            <div className="border-t border-background-200 pt-4">
              <p className="text-sm font-label font-medium text-foreground-800">Changer le mot de passe</p>
              <div className="mt-3 grid grid-cols-1 gap-3">
                <Field label="Mot de passe actuel" htmlFor="pwd-current">
                  <PasswordInput id="pwd-current" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Nouveau mot de passe" htmlFor="pwd-next">
                    <PasswordInput id="pwd-next" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} />
                  </Field>
                  <Field label="Confirmation" htmlFor="pwd-confirm">
                    <PasswordInput id="pwd-confirm" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
                  </Field>
                </div>
              </div>
              {pwdError && <p className="mt-2 text-xs text-primary-700">{pwdError}</p>}
              {pwdSaved && (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-label font-medium text-secondary-700">
                  <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
                  Mot de passe mis à jour.
                </p>
              )}
              <button
                type="button"
                onClick={() => void handlePassword()}
                disabled={pwdSaving}
                className="mt-3 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-lock-password-line text-base" aria-hidden="true" />
                Mettre à jour le mot de passe
              </button>
            </div>

            <div className="border-t border-background-200 pt-4">
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
              >
                <i className="ri-logout-box-r-line text-base" aria-hidden="true" />
                Se déconnecter
              </button>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}